import { useState, useEffect, useMemo, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  Result,
  InputDiv,
  MathDiv,
  ActionButtonsRow,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  Select,
  CopyHTMLButton,
  CheckboxWrapper,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
export function MansoryCompressive() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const mansoryGradient = 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)'
  const mansoryUnit = useMemo(
    () => [
      { type: 1, text: 'Concreto' },
      { type: 2, text: 'Cerâmico vazado' },
      { type: 3, text: 'Cerâmico maciço' },
    ],
    []
  )
  const restraintsOptions = useMemo(() => ['Travamentos nas duas extremidades', 'Travamento em uma extremidades'], [])

  const [fkfpk, setFkfpk] = useState(0.7)
  const [gammam, setGammam] = useState(2)
  const [gammaf, setGammaf] = useState(1.4)
  const [isOnlySide, setIsOnlySide] = useState(false)
  const [t, setT] = useState('')
  const [l, setL] = useState('')
  const [h, setH] = useState('')
  const [distributedLoad, setDistributedLoad] = useState('')
  const [gammaalv, setGammaalv] = useState(15)
  const [selectedType, setSelectedType] = useState('Concreto')
  const [verticalRestraint, setVerticalRestraint] = useState('Travamentos nas duas extremidades')
  const [horizontalRestraint, setHorizontalRestraint] = useState('Travamentos nas duas extremidades')
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [touched, setTouched] = useState({})
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const handleTypeChange = (event) => {
    const selectedType = event.target.value
    setSelectedType(selectedType)
  }

  const handleVerticalRestraint = (event) => {
    const selected = event.target.value
    setVerticalRestraint(selected)
  }

  const handleHorizontalRestraint = (event) => {
    const selected = event.target.value
    setHorizontalRestraint(selected)
  }

  const HISTORY_KEY = 'mansory-bending-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]

      setFkfpk(entry.fkfpk)
      setGammam(entry.gammam)
      setGammaf(entry.gammaf)
      setIsOnlySide(entry.isOnlySide)
      setT(entry.t)
      setL(entry.l)
      setH(entry.h)
      setDistributedLoad(entry.distributedLoad)
      setGammaalv(entry.gammaalv)
      setSelectedType(entry.selectedType)
      setVerticalRestraint(entry.verticalRestraint)
      setHorizontalRestraint(entry.horizontalRestraint)
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (name !== 'gammaalv' && numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fkfpk = validateField('fkfpk', fkfpk)
    newErrors.gammam = validateField('gammam', gammam)
    newErrors.gammaf = validateField('gammaf', gammaf)
    newErrors.t = validateField('t', t)
    newErrors.l = validateField('l', l)
    newErrors.h = validateField('h', h)
    newErrors.distributedLoad = validateField('distributedLoad', distributedLoad)
    newErrors.gammaalv = validateField('gammaalv', gammaalv)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fkfpk, gammam, gammaf, t, l, h, distributedLoad, gammaalv, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      event.preventDefault()

      const type = mansoryUnit.find((mu) => mu.text === selectedType).type

      const data = {
        type,
        fkfpk,
        gammam,
        gammaf,
        isOnlySide,
        t,
        h,
        l,
        verticalRestraint: restraintsOptions.findIndex((r) => r == verticalRestraint),
        horizontalRestraint: restraintsOptions.findIndex((r) => r == horizontalRestraint),
        distributedLoad,
        gammaalv,
        selectedType,
      }

      setData(data)

      const response = await api.post('/mansory/compressive', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fkfpk,
        gammam,
        gammaf,
        isOnlySide,
        t,
        l,
        h,
        distributedLoad,
        gammaalv,
        selectedType,
        verticalRestraint,
        horizontalRestraint,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      fkfpk,
      gammam,
      gammaf,
      isOnlySide,
      t,
      l,
      h,
      distributedLoad,
      gammaalv,
      selectedType,
      verticalRestraint,
      horizontalRestraint,
      result,
      data,
      isFormValid,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'state.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setFkfpk(jsonData.fkfpk)
        setGammam(jsonData.gammam)
        setGammaf(jsonData.gammaf)
        setIsOnlySide(jsonData.isOnlySide)
        setT(jsonData.t)
        setL(jsonData.l)
        setH(jsonData.h)
        setDistributedLoad(jsonData.distributedLoad)
        setGammaalv(jsonData.gammaalv)
        setSelectedType(jsonData.selectedType)
        setVerticalRestraint(jsonData.verticalRestraint)
        setHorizontalRestraint(jsonData.horizontalRestraint)
        setResult(jsonData.result)
        setData(jsonData.data)
        setIsFormValid(jsonData.isFormValid)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o arquivo. Verifique o formato.')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
      errorMessage: 'Erro ao copiar os cálculos. Tente novamente.',
    })
  }

  return (
    <PageContainer>
      <PageHeader $gradient={mansoryGradient}>
        <div className="header-content">
          <BackButton to="/alvenaria">
            <FiArrowLeft /> Voltar para Alvenaria
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/alvenaria">Alvenaria</a>
            <span>/</span>
            <span>Compressão</span>
          </div>
          <h1>🧱 Compressão em Alvenaria Estrutural</h1>
          <p className="subtitle">
            Dimensionamento e verificação de elementos de alvenaria estrutural submetidos à compressão simples
          </p>
          <div className="badges">
            <Badge>📋 NBR 16868-1:2020</Badge>
            <Badge>🧮 Esbeltez e Flambagem</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard>
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza o dimensionamento de elementos de alvenaria estrutural submetidos à compressão
              simples, conforme a NBR 16868-1:2020.
            </p>
            <p>
              O cálculo considera os efeitos de esbeltez e flambagem, determinando a resistência característica do
              prisma de alvenaria necessária para suportar os carregamentos aplicados, levando em conta as condições de
              contorno e a geometria do elemento.
            </p>
            <div className="norms">
              <span>NBR 16868-1:2020</span>
              <span>Análise de Estabilidade</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <h3>Dados de uso</h3>
          <form onSubmit={handleCalculate}>
            <InputDiv>
              <span>Tipo de bloco</span>
              <div>
                <Select style={{ marginLeft: '0' }} id="mansorySelect" value={selectedType} onChange={handleTypeChange}>
                  {mansoryUnit.map((mu, index) => (
                    <option key={index} value={mu.text}>
                      {mu.text}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Relação fk/fpk</span>
              <ValidatedInput
                name="fkfpk"
                type="number"
                value={fkfpk || ''}
                onChange={(event) => setFkfpk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>
                &gamma;<sub>m</sub>
              </span>
              <ValidatedInput
                name="gammam"
                type="number"
                value={gammam || ''}
                onChange={(event) => setGammam(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Argamassa apenas nos cordões laterais</span>
              <div>
                <CheckboxWrapper>
                  <input
                    type="checkbox"
                    checked={isOnlySide}
                    onChange={(event) => setIsOnlySide(event.target.checked)}
                  />
                </CheckboxWrapper>
              </div>
            </InputDiv>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Espessura efetiva (cm)</span>
              <ValidatedInput
                name="t"
                type="number"
                value={t || ''}
                onChange={(event) => setT(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura (cm)</span>
              <ValidatedInput
                name="h"
                type="number"
                value={h || ''}
                onChange={(event) => setH(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Comprimento (cm)</span>
              <ValidatedInput
                name="l"
                type="number"
                value={l || ''}
                onChange={(event) => setL(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Restrições</h3>
            <InputDiv>
              <span>
                Coeficente de esbeltez vertical &alpha;<sub>v</sub>
              </span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  id="verticalRestraintSelect"
                  value={verticalRestraint}
                  onChange={handleVerticalRestraint}
                >
                  {restraintsOptions.map((text, index) => (
                    <option key={index} value={text}>
                      {text}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            <InputDiv>
              <span>
                Coeficente de esbeltez horizontal &alpha;<sub>h</sub>
              </span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  id="horizontalRestraintSelect"
                  value={horizontalRestraint}
                  onChange={handleHorizontalRestraint}
                >
                  {restraintsOptions.map((text, index) => (
                    <option key={index} value={text}>
                      {text}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            <h3>Carregamentos</h3>
            <InputDiv>
              <span>Carregamento Característico (kN/m)</span>
              <ValidatedInput
                name="distributedLoad"
                type="number"
                value={distributedLoad || ''}
                onChange={(event) => setDistributedLoad(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Coefienciete de ponderação &gamma;f</span>
              <ValidatedInput
                name="gammaf"
                type="number"
                value={gammaf || ''}
                onChange={(event) => setGammaf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>
                &gamma;<sub>alv</sub> (kN/m³)
              </span>
              <ValidatedInput
                name="gammaalv"
                type="number"
                value={gammaalv}
                onChange={(event) => setGammaalv(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <MathDiv>
                <BlockMath
                  math={`f_{pk}\\geq ${result.fpk.toFixed(0)}\\, kN/m² = ${(result.fpk / 1000).toFixed(2)} \\, MPa`}
                />
                {isOnlySide && (
                  <BlockMath
                    math={`f_{pk}\\geq \\dfrac{${(result.fpk / 1000).toFixed(2)}}{0,64}=${result.fpkMPa} \\, MPa`}
                  />
                )}
                <p>Tipo de bloco: {data.selectedType}</p>
                <BlockMath math={`f_{bk}= ${result.block.unitStrength} \\, MPa`} />
                <BlockMath math={`f_{pk}= ${result.block.prismStrength} \\, MPa`} />
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Coeficente de flambagem</h3>
              <MathDiv>
                <BlockMath>{String.raw`h_{ef} \leq \begin{cases} \alpha_v \cdot h \\ 0,7 \cdot \left( \alpha_v \cdot h \cdot \alpha_h \cdot l \right)^{1/2} \end{cases}`}</BlockMath>
                <BlockMath>{String.raw`h_{ef} \leq \begin{cases} ${result.alphav} \cdot ${data.h} \\ 0,7 \cdot \left( ${result.alphav} \cdot ${data.h} \cdot ${result.alphah} \cdot ${data.h} \right)^{1/2} \end{cases}`}</BlockMath>
                <BlockMath>{String.raw`h_{ef} \leq \begin{cases} ${result.hefResponse.aPart.toFixed(2)} \\ ${result.hefResponse.bPart.toFixed(2)} \end{cases}=${result.hefResponse.hef.toFixed(2)} \, cm`}</BlockMath>
                <BlockMath
                  math={`\\lambda=\\dfrac{h_{ef}}{t_{ef}}=\\dfrac{${result.hefResponse.hef.toFixed(2)}}{${data.t}}=${result.lambda}`}
                />
              </MathDiv>
              <h3>Carregamentos</h3>
              <MathDiv>
                <BlockMath math={`p=Q + (\\gamma_{alv} \\cdot h \\cdot t)`} />
                <BlockMath
                  math={`p=${data.distributedLoad} + (${data.gammaalv} \\cdot ${data.h / 100} \\cdot ${data.t / 100})`}
                />
                <BlockMath math={`p=${result.totalDistributedLoad.toFixed(2)} \\, kN/m`} />
                <BlockMath math={`F_k=p \\cdot L=${result.totalDistributedLoad.toFixed(2)} \\cdot ${data.l / 100}`} />
                <BlockMath math={`F_k=${result.totalLoad.toFixed(2)} \\, kN`} />
              </MathDiv>
              <h3>Tensões</h3>
              <MathDiv>
                <BlockMath math={`R=\\left[1- {\\left( \\dfrac{h_{ef}}{40 - t_{ef}} \\right)}^3 \\right]`} />
                <BlockMath
                  math={`R=\\left[1- {\\left( \\dfrac{${result.hefResponse.hef.toFixed(3)}}{40 - ${data.t}} \\right)}^3 \\right]`}
                />
                <BlockMath math={`R=${result.r.toFixed(4)}`} />
                <BlockMath math={`A=t \\cdot l=${data.t} \\cdot ${data.l}`} />
                <BlockMath math={`A=${result.area} \\, cm^2`} />
                <BlockMath
                  math={`\\sigma=\\dfrac{\\gamma_f \\cdot F_k}{A}=\\dfrac{${data.gammaf} \\cdot ${result.totalLoad.toFixed(2)}}{${result.area / 10000}}`}
                />
                <BlockMath math={`\\sigma=${result.appliedStress.toFixed(2)} \\, kN/m^2`} />
                <BlockMath math={`\\sigma \\leq \\dfrac{f_k}{\\gamma_m}\\cdot R`} />
                <BlockMath
                  math={`${result.appliedStress.toFixed(2)} \\leq \\dfrac{${data.fkfpk} \\cdot f_{pk}}{${data.gammam}}\\cdot ${result.r.toFixed(4)}`}
                />
                <BlockMath
                  math={`f_{pk}\\geq ${result.fpk.toFixed(0)}\\, kN/m² = ${(result.fpk / 1000).toFixed(2)} \\, MPa`}
                />
                {isOnlySide && (
                  <>
                    <h4>Redução pela apliação da argamassa</h4>
                    <BlockMath
                      math={`f_{pk}\\geq \\dfrac{${(result.fpk / 1000).toFixed(2)}}{0,64}=${result.fpkMPa} \\, MPa`}
                    />
                  </>
                )}
              </MathDiv>
              <h3>Seleção do bloco</h3>
              <MathDiv>
                <p>Tipo de bloco: {data.selectedType}</p>
                <BlockMath math={`f_{bk}= ${result.block.unitStrength} \\, MPa`} />
                <BlockMath math={`f_{pk}= ${result.block.prismStrength} \\, MPa`} />
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
